/* eslint-disable no-unused-vars */
import Supercluster from 'supercluster';
import { createTextGl, zoomToFitBox2D } from '../../utils/forgeUtils';
import store from '@/store';
import { UI_THRESHOLDS } from '@/constants/apiLimits';
const THREE = window.THREE;

/**
 * Base class for tag handlers that provides common functionality for
 * processing, clustering, and rendering different types of tags in the viewer.
 * This class is meant to be extended by specific tag type handlers.
 */
export class TagHandlerBase {
	// Constants for clustering parameters
	static MIN_ZOOM_SCALE = 0.1;          // Minimum zoom scale before skipping clustering
	static MAX_ZOOM_SCALE = 1.2;          // Maximum zoom scale for normalization
	static MAX_CLUSTER_ZOOM = 16;         // Maximum zoom level for Supercluster
	static MIN_CLUSTER_FACTOR = 0.01;     // Minimum cluster factor for radius calculation
	static MAX_CLUSTER_FACTOR = 0.2;      // Maximum cluster factor for radius calculation
	static TILE_SIZE_PX = 8192;           // Pixel size for tile calculations
	static Z_TOLERANCE = 1e-6;            // Tolerance for Z-axis filtering

	/**
	 * Create a tag handler
	 * @param {Object} extension - The parent extension that manages all tag handlers
	 */
	constructor(extension) {
		this.extension = extension;
		this.cluster = null; // Supercluster instance for clustering
		// Store cluster text meshes specific to this handler
		this.clusterTextMeshes = [];
		this.tagIdMap = new Map();
		this.bbox = null; // Bounding box for clustering
		// Cache for clustering results
		this.clusterCache = {
			lastCameraZ: null,
			lastTags: null,
			lastRange: null,
			result: null,
			timestamp: 0
		};
	}

	/**
	 * Get viewer type (2D or 3D)
	 * @returns {boolean} True if the current viewer is 2D, false if 3D
	 */
	isViewer2d() {
		return this.extension.isViewer2d();
	}

	/**
	 * Draw tag area by delegating to parent extension
	 * @param {Object} tag - Tag object with position and geometry information
	 */
	drawTagArea(tag) {
		this.extension.drawTagArea(tag);
	}

	/**
	 * Check if tag type should be processed based on display settings
	 * This is a base method that should be overridden in subclasses
	 * @param {string} type - Tag type identifier (e.g., 'equipment', 'structural', etc.)
	 * @param {Object} settings - Display settings containing visibility flags
	 * @returns {boolean} True if the tag should be processed, false otherwise
	 */
	shouldProcess(type, settings) {
		return false; // Override in subclasses
	}

	/**
	 * Collect tag data for visualization based on settings and viewer
	 * This is a base method that should be overridden in subclasses
	 * @param {Object} settings - Display settings and filter options
	 * @param {boolean} settings.isGlobalSearchFilter - Whether global search filter is active
	 * @param {boolean} settings.isGlobalSearchTabShown - Whether global search tab is shown
	 * @param {Object} settings.displaySettings - Settings for tag display visibility
	 * @param {Object} viewerData - Viewer position and camera data
	 * @returns {Object} Collection of tag data organized by category
	 */
	collectData(settings, viewerData) {
		return {}; // Override in subclasses
	}

	/**
	 * Clear all text meshes created by this handler for clusters
	 * This removes cluster count indicators from the scene
	 */
	clearClusterTextMeshes() {
		if (this.clusterTextMeshes.length > 0) {
			this.extension.removeFromScene(this.clusterTextMeshes, this.extension.viewer, this.extension.sceneName);
			this.clusterTextMeshes = [];
		}
	}

	/**
	 * Process clusters of items in 2D view, creating visual representations
	 * @param {Array} clusters - Clusters of items, where each cluster is an array of items 
	 * @param {Object} data - Data collection object to store processed clusters
	 * @param {string} clusterKey - Key to use for storing clusters in data object
	 */
	processClusters(clusters, data, clusterKey) {
		const _this = this;

		clusters.forEach(list => {
			// Calculate the center point of the cluster
			let points = list.map(o => store.state.selectedLevel.mappingViewersPointsInfo._3dTo2d(o.point));
			var box = new THREE.Box3().setFromPoints(list.map(o => o.point));
			var center = box.getCenter();
			var pt = store.state.selectedLevel.mappingViewersPointsInfo._3dTo2d(center);
			pt.z = .1;

			// Create cluster data
			let clusterData = {
				name: list.length,
				points: points,
				point: pt,
				clusterCreate: () => {
					// Create text to show count
					let size = 12 / (_this.extension.viewer.worldToClient(new THREE.Vector2(1, 0)).x -
						_this.extension.viewer.worldToClient(new THREE.Vector2(0, 0)).x);
					let textPt = new THREE.Vector3(pt.x, pt.y, pt.z + .05);
					let textMesh = createTextGl(list.length, size, 0xffffff, textPt,
						_this.extension.viewer, _this.extension.sceneName);
					_this.clusterTextMeshes.push(textMesh);
				},
				onClick: () => {
					// Fit view to cluster bounds when clicked
					var min = store.state.selectedLevel.mappingViewersPointsInfo._3dTo2d(box.min);
					var max = store.state.selectedLevel.mappingViewersPointsInfo._3dTo2d(box.max);
					var clusterBox = new THREE.Box3(min, max);
					zoomToFitBox2D(_this.extension.viewer, clusterBox);
				}
			};

			// Add to collection with cluster key
			data[clusterKey] ? data[clusterKey].push(clusterData) : data[clusterKey] = [clusterData];
		});
	}

	/**
	 * Clusters tags in 2D space using Supercluster, optimized for current zoom level
	 * This creates dynamic tag clustering based on camera position and density
	 *
	 * @param {Array} tags - Array of tag objects (each with .point property in world 3D)
	 * @param {Object} cameraPosition - Viewer camera position (from viewer.navigation.getPosition())
	 * @param {number} [range=1] - Multiplier on clustering radius to adjust clustering density
	 * @param {boolean} [ignoreZ=true] - If true, cluster by X/Y only; otherwise include Z
	 * @returns {{ items: Object[], clusters: Object[][] }} Object containing separate items and clusters
	 */
	getTagsClustersAndItems2D(tags, cameraPosition, range = 1, ignoreZ = true, forceUpdate = false) {
		// Early exit
		if (!tags?.length || !store.state.selectedLevel?.mappingViewersPointsInfo) {
			return { items: [], clusters: [] };
		}

		// Step 1: Read current zoom from orthoScale (2D) or fallback to cameraPosition.z
		const cam = this.extension.viewer.getCamera();
		const orthoScale = (cam.orthoScale !== undefined)
			? cam.orthoScale
			: (cameraPosition?.z ?? 1);

		// Step 2: If extremely zoomed in, skip clustering
		if (orthoScale < TagHandlerBase.MIN_ZOOM_SCALE || tags.length < UI_THRESHOLDS.TAG_CLUSTER_THRESHOLD) {
			const skip = { items: tags, clusters: [] };
			return skip;
		}

		// Step 3: Compute scene diagonal & normalized zoom [0–1]
		const normZoom = Math.min(
			Math.max(
				(orthoScale - TagHandlerBase.MIN_ZOOM_SCALE) /
				(TagHandlerBase.MAX_ZOOM_SCALE - TagHandlerBase.MIN_ZOOM_SCALE),
				0
			),
			1
		);

		// Step 4: Decide Supercluster zoom level (0 = far out / big clusters, MAX_CLUSTER_ZOOM = close in)
		const scZoom = Math.round((1 - normZoom) * TagHandlerBase.MAX_CLUSTER_ZOOM);

		// Step 5: Project all tags to 2D (and retain Z if needed)
		if (this.tagIdMap.size != tags.length || forceUpdate) {
			this.tagIdMap.clear();
			let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
			const features = tags.map((tag, i) => {
				const id = tag.id || `tag_${i}`;
				this.tagIdMap.set(id, tag);
				const p2 = store.state.selectedLevel.mappingViewersPointsInfo._3dTo2d(tag.point);
				minX = Math.min(minX, p2.x); maxX = Math.max(maxX, p2.x);
				minY = Math.min(minY, p2.y); maxY = Math.max(maxY, p2.y);
				return {
					type: 'Feature',
					properties: { id, z: tag.point.z },
					geometry: { type: 'Point', coordinates: [p2.x, p2.y] }
				};
			});

			this.bbox = [minX, minY, maxX, maxY];

			// Expand bbox by 10% to avoid edge cases
			const dx = ((maxX - minX) || 1) * 0.1;
			const dy = ((maxY - minY) || 1) * 0.1;
			this.bbox[0] -= dx;
			this.bbox[1] -= dy;
			this.bbox[2] += dx;
			this.bbox[3] += dy;
			this.bbox = this.bbox.map(v => Math.round(v * 1000) / 1000); // Round to 3 decimal places

			// Step 6: Compute cluster radius in pixels (map world units → tile pixels)
			// Factor moves radius within [MIN_CLUSTER_FACTOR … MAX_CLUSTER_FACTOR] of diag
			const factor = TagHandlerBase.MIN_CLUSTER_FACTOR +
				normZoom * (TagHandlerBase.MAX_CLUSTER_FACTOR - TagHandlerBase.MIN_CLUSTER_FACTOR);
			const radiusPx = Math.max(1, Math.round(factor * range * TagHandlerBase.TILE_SIZE_PX));

			// Step 7: Build & load Supercluster
			this.cluster = new Supercluster({
				radius: radiusPx,
				maxZoom: TagHandlerBase.MAX_CLUSTER_ZOOM,
				minZoom: 0,
			});

			this.cluster.load(features);
		}

		const raw = this.cluster.getClusters(this.bbox, scZoom);

		// Step 8: Separate into item IDs & cluster-ID arrays
		const separatedIds = [];
		const idClusters = [];

		raw.forEach(feat => {
			if (feat.properties.cluster) {
				// Expand cluster to all leaf points
				const leaves = this.cluster.getLeaves(feat.properties.cluster_id, Infinity);
				const ids = leaves
					.filter(l => ignoreZ || Math.abs(l.properties.z - feat.properties.z) < TagHandlerBase.Z_TOLERANCE)
					.map(l => l.properties.id);
				if (ids.length > 1) {
					idClusters.push(ids);
				} else {
					// degenerate: only one left after z‑filter => treat as separate
					if (ids.length > 0) {
						separatedIds.push(ids[0]);
					}
				}
			} else {
				// single point
				separatedIds.push(feat.properties.id);
			}
		});

		// Step 9: Return result
		const idResult = {
			items: separatedIds,
			clusters: idClusters,
			_idMap: Array.from(this.tagIdMap.entries())
		};

		return this._idsToObjects(idResult, tags);
	}

	/**
	 * Reset cached data for clustering to force recomputation
	 * Call this when tag data changes or on viewport reset
	 */
	resetClusterCache() {
		this.tagIdMap.clear();
		this.bbox = null;
		this.cluster = null;
		this.clusterCache = {
			lastCameraZ: null,
			lastTags: null,
			lastRange: null,
			result: null,
			timestamp: 0
		};
	}

	/**
	 * Convert ID-based clustering result to object-based result
	 * @param {Object} idResult - Result with IDs
	 * @param {Array} tags - Original tag objects
	 * @returns {Object} Result with objects instead of IDs
	 */
	_idsToObjects(idResult, tags) {
		// If there's no ID map in the result, create one from tags
		const idMap = idResult._idMap ?
			new Map(idResult._idMap) :
			new Map(tags.map((tag, i) => [tag.id || `tag_${i}`, tag]));

		// Convert items
		const items = idResult.items.map(id => idMap.get(id));

		// Convert clusters
		const clusters = idResult.clusters.map(cluster =>
			cluster.map(id => idMap.get(id))
		);

		return { items, clusters };
	}

	/**
	 * Update cache with ID-based results
	 * @param {Array} tags - Original tag objects
	 * @param {string|number} cameraPositionKey - Key representing camera position for cache
	 * @param {string|number} tagCountKey - Key representing tag count for cache
	 * @param {number} range - Clustering range parameter
	 * @param {Object} result - Clustering result to cache
	 */
	_updateCache(tags, cameraPositionKey, tagCountKey, range, result) {
		this.clusterCache = {
			cameraPositionKey,
			tagCountKey,
			range,
			result,
			timestamp: Date.now()
		};
	}
}
